import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  X,
  Users,
  Phone,
  Copy,
  Check,
  ClipboardList,
  AlertCircle,
} from "lucide-react";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import { fetchBusinessCustomers } from "../../../../services/thunks/BusinessThunk";
import "./styles/CustomersModal.css";

interface CustomersModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  businessName?: string;
}

/** Pastel avatar palette — index-stable so a number keeps its color. */
const AVATAR_COLORS = [
  "#ede9fe", "#dbeafe", "#dcfce7", "#fef9c3", "#ffe4e6",
  "#e0f2fe", "#fae8ff", "#fef3c7", "#d1fae5", "#fee2e2",
];
const AVATAR_TEXT = [
  "#6d28d9", "#1d4ed8", "#15803d", "#a16207", "#be123c",
  "#0369a1", "#a21caf", "#b45309", "#047857", "#b91c1c",
];

const CustomersModal: React.FC<CustomersModalProps> = ({
  isOpen,
  onClose,
  businessId,
  businessName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const customers: string[] = useSelector((s: any) => s.business.customers);
  const isLoading = useSelector(
    (s: any) => !!s.business.loadingMap.fetchBusinessCustomers
  );
  const error = useSelector(
    (s: any) => s.business.errorMap.fetchBusinessCustomers
  );

  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  // Fetch fresh numbers each time the modal opens; reset transient copy state.
  useEffect(() => {
    if (isOpen && businessId) {
      setCopiedIndex(null);
      setCopiedAll(false);
      dispatch(fetchBusinessCustomers(businessId));
    }
  }, [isOpen, businessId, dispatch]);

  if (!isOpen) return null;

  const copyOne = async (number: string, index: number) => {
    try {
      await navigator.clipboard.writeText(number);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex((cur) => (cur === index ? null : cur)), 1600);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  const copyAllCsv = async () => {
    try {
      await navigator.clipboard.writeText(customers.join(","));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  return (
    <div className="customers-modal__overlay" onClick={onClose}>
      <div
        className="customers-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Customers"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="customers-modal__header">
          <div className="customers-modal__title-group">
            <div className="customers-modal__title-icon">
              <Users size={18} />
            </div>
            <div>
              <h2 className="customers-modal__title">Customers</h2>
              <p className="customers-modal__subtitle">
                {isLoading
                  ? "Loading…"
                  : `${customers.length} phone number${customers.length !== 1 ? "s" : ""}`}
                {businessName ? ` · ${businessName}` : ""}
              </p>
            </div>
          </div>
          <button
            className="customers-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="customers-modal__list">
          {isLoading && (
            <div className="customers-modal__skeleton">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="customers-modal__skeleton-row">
                  <span className="customers-modal__skeleton-avatar" />
                  <span className="customers-modal__skeleton-line" />
                </div>
              ))}
            </div>
          )}

          {!isLoading && error && (
            <div className="customers-modal__state">
              <AlertCircle size={32} color="#f87171" />
              <p>{error}</p>
              <button
                className="customers-modal__retry"
                onClick={() => dispatch(fetchBusinessCustomers(businessId))}
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && customers.length === 0 && (
            <div className="customers-modal__state">
              <Users size={32} color="#cbd5e1" />
              <p>No customers yet.</p>
            </div>
          )}

          {!isLoading &&
            !error &&
            customers.map((number, i) => {
              const isCopied = copiedIndex === i;
              return (
                <button
                  key={`${number}-${i}`}
                  className={`customers-modal__row ${isCopied ? "customers-modal__row--copied" : ""}`}
                  onClick={() => copyOne(number, i)}
                  title="Tap to copy"
                >
                  <span
                    className="customers-modal__avatar"
                    style={{
                      background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                      color: AVATAR_TEXT[i % AVATAR_TEXT.length],
                    }}
                  >
                    <Phone size={14} />
                  </span>
                  <span className="customers-modal__number">{number}</span>
                  <span
                    className={`customers-modal__copy-hint ${isCopied ? "customers-modal__copy-hint--done" : ""}`}
                  >
                    {isCopied ? (
                      <>
                        <Check size={14} /> Copied
                      </>
                    ) : (
                      <Copy size={14} />
                    )}
                  </span>
                </button>
              );
            })}
        </div>

        {/* ── Copy all ── */}
        {!isLoading && !error && customers.length > 0 && (
          <div className="customers-modal__footer">
            <button
              className={`customers-modal__copy-all ${copiedAll ? "customers-modal__copy-all--done" : ""}`}
              onClick={copyAllCsv}
            >
              {copiedAll ? (
                <>
                  <Check size={17} /> Copied {customers.length} numbers
                </>
              ) : (
                <>
                  <ClipboardList size={17} /> Copy all as CSV
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersModal;
